import { Card } from 'components/Card';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import {
  ModalScreenProps,
  ModalScreens,
  ScreenProps,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { cloneDeep, isEqual, startCase } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { doAseUpdateInstanceCheckResult } from 'store/assessments/actions';
import {
  selectAseTplFieldInstanceCheck,
  selectAseResultFieldInstanceCheck,
  selectAseFieldInstanceAseMerged,
  selectAseTplFieldInstanceDefaultChecksForGroup,
} from 'store/assessments/selectors';
import {
  AseFieldInstanceCheckStatusEnum,
  AseFieldInstanceStatuses,
  AseResultInstanceCheck,
  FieldInstanceCheckScheduleStatusEnum,
} from 'store/assessments/types';
import { useEsc } from 'hooks/useEsc';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { CardRow } from 'components/CardRow';
import { str } from 'lib/str';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { formatDate, numberToDate } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { ICONS } from 'components/Icon';

export const AseInstanceCheckEdit: React.FC<
  | ModalScreenProps<ModalScreens.AseInstanceCheck>
  | ScreenProps<Stacks.ASEREPEATABLE, Screens.AseInstanceCheck>
> = ({ navigation, route, isModal }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const screenSize = useScreenSize();
  const forceUpdate = useForceUpdate();
  const { id, instanceId } = route.params;

  const check = useSelector((state) =>
    selectAseTplFieldInstanceCheck(state, id),
  );
  const selectedData = useSelector((state) =>
    selectAseResultFieldInstanceCheck(state, id, instanceId),
  ) || {
    checkId: id,
    instanceId,
    remarks: '',
    statusId: AseFieldInstanceCheckStatusEnum.completed,
  };
  const dataRef = React.useRef<AseResultInstanceCheck>(cloneDeep(selectedData));
  const initDataRef = React.useRef<AseResultInstanceCheck>(
    cloneDeep(dataRef.current),
  );
  const internalSave = !!isModal;

  if (!internalSave && selectedData) {
    dataRef.current = selectedData;
    initDataRef.current = dataRef.current;
  }

  const checkResult: AseResultInstanceCheck = internalSave
    ? dataRef.current
    : selectedData;

  const instance = useSelector((state) =>
    selectAseFieldInstanceAseMerged(state, instanceId),
  );

  const actualCheckData = instance?.checks?.[id];
  const defaultCheckData = useSelector((state) =>
    selectAseTplFieldInstanceDefaultChecksForGroup(
      state,
      instance?.groupId || '-1',
    ),
  )[id];
  const checkData = actualCheckData || defaultCheckData;

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  isDirtyRef.current = checkIsDirty();

  const handleHardSave = React.useCallback(() => {
    if (checkIsDirty()) {
      dispatch(doAseUpdateInstanceCheckResult(dataRef.current));
    } else if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [checkIsDirty, dispatch, isModal, navigation, screenSize]);

  const handleClose = React.useCallback(() => {
    if (isModal) {
      if (checkIsDirty()) {
        confirmUnsaved({
          onSave: handleHardSave,
          onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
        });
        return;
      }
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkIsDirty,
    confirmUnsaved,
    dispatch,
    handleHardSave,
    isModal,
    navigation,
    screenSize,
  ]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<AseResultInstanceCheck>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        if (internalSave) {
          dataRef.current = { ...dataRef.current, ...changes };
          if (
            doForceUpdate === 'always' ||
            (doForceUpdate === 'dirtyChange' &&
              checkIsDirty() !== isDirtyRef.current)
          ) {
            forceUpdate();
          }
        } else {
          dispatch(doAseUpdateInstanceCheckResult(newValues));
        }
      }
    },
    [checkIsDirty, dispatch, forceUpdate, internalSave],
  );

  const saveField = React.useCallback(
    (name: keyof AseResultInstanceCheck, value: string) =>
      handleSoftSave({ [name]: value }, { doForceUpdate: 'dirtyChange' }),
    [handleSoftSave],
  );

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(isModal && handleClose);

  if (!check) {
    return null;
  }

  const lastCompleted = checkData?.lastCompleted;
  const lastAttempted = checkData?.lastAttempted;
  const due = checkData?.schedule.due;

  return (
    <PageScrollView
      isInModal={false}
      header={
        <NavigationHeader
          onPressSaveButton={isModal ? handleHardSave : undefined}
          onPressCancelButton={isModal ? handleClose : undefined}
          onPressBackButton={
            !isModal && screenSize === 'small' ? navigation?.goBack : undefined
          }
          heading={check.name}
        />
      }
    >
      <Card label="Overview">
        <CardRow
          labelAlignTop
          label={`Due ${startCase(check.type.period)}`}
          value={
            due
              ? `Current Period: ${formatDate(
                  numberToDate(due.from),
                  UI_DATE_FORMAT_LONG,
                )} – ${formatDate(numberToDate(due.to), UI_DATE_FORMAT_LONG)}`
              : undefined
          }
        />
        {checkData?.schedule.statusId ===
        FieldInstanceCheckScheduleStatusEnum.completed ? (
          <CardRow
            labelAlignTop
            label="Check Not Required"
            value="A check has already been completed in this period"
            navigationIcon={ICONS.OctagonCheckSolid}
          />
        ) : (
          <CardRow
            labelAlignTop
            label="Check Required"
            value="A check has not been completed in this period"
          />
        )}
      </Card>
      <Card>
        <CardRow
          label="Last Completed"
          value={
            lastCompleted
              ? formatDate(
                  numberToDate(lastCompleted.date),
                  UI_DATE_FORMAT_LONG,
                )
              : 'Never'
          }
          valueColor={
            !lastCompleted || (due && lastCompleted.date < due.from)
              ? theme.colors.red
              : theme.colors.green
          }
        />
        {lastAttempted &&
          (!lastCompleted || lastCompleted.date < lastAttempted.date) && (
            <CardRow
              label="Last Attempted"
              value={formatDate(
                numberToDate(lastAttempted.date),
                UI_DATE_FORMAT_LONG,
              )}
            />
          )}
      </Card>

      <SeparatorHorizontal withMarginBottom />

      <Card
        label="In this assessment..."
        explanation={
          `${
            checkData?.schedule.statusId ===
            FieldInstanceCheckScheduleStatusEnum.completed
              ? `This check has already been completed; choosing ${str.dQuote(
                  'Attempted',
                )} will not affect this status.`
              : `Choosing ${str.dQuote(
                  'Attempted',
                )} will not complete this check.`
          } ` +
          "Explain why the check couldn't be completed in the remarks (e.g. access issues, etc)"
        }
      >
        <CardRow
          label={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.completed]
              .label
          }
          navigationIcon={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.completed]
              .icon
          }
          navigationIconColor={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.completed]
              .color
          }
          radioValue={
            checkResult.statusId === AseFieldInstanceCheckStatusEnum.completed
          }
          onPress={() =>
            handleSoftSave(
              { statusId: AseFieldInstanceCheckStatusEnum.completed },
              { doForceUpdate: 'always' },
            )
          }
          labelAlignTop
        />
        <CardRow
          label={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.attempted]
              .label
          }
          onPress={() =>
            handleSoftSave(
              { statusId: AseFieldInstanceCheckStatusEnum.attempted },
              { doForceUpdate: 'always' },
            )
          }
          navigationIcon={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.attempted]
              .icon
          }
          navigationIconColor={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.attempted]
              .color
          }
          radioValue={
            checkResult.statusId === AseFieldInstanceCheckStatusEnum.attempted
          }
          labelAlignTop
        />
        <CardRow
          label={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.skipped]
              .label
          }
          onPress={() =>
            handleSoftSave(
              { statusId: AseFieldInstanceCheckStatusEnum.skipped },
              { doForceUpdate: 'always' },
            )
          }
          navigationIcon={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.skipped]
              .icon
          }
          navigationIconColor={
            AseFieldInstanceStatuses[AseFieldInstanceCheckStatusEnum.skipped]
              .color
          }
          radioValue={
            checkResult.statusId === AseFieldInstanceCheckStatusEnum.skipped
          }
          labelAlignTop
        />
      </Card>
      <Card>
        <TextAreaField
          placeholder="Remarks"
          value={checkResult.remarks}
          onChangeWithName={saveField}
          name="remarks"
          onEsc={handleClose}
          autoFocusWeb={isModal}
          autoFocusDelay
        />
      </Card>
    </PageScrollView>
  );
};
